import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import Layout, { Card, Label, Badge, Spinner } from "../components/Layout";
import { CheckCircle, Circle, Clock, ArrowRight, Lightning, BookOpen } from "@phosphor-icons/react";

export default function ProceduresList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/procedures").then(({ data }) => { setItems(data); setLoading(false); }); }, []);

  if (loading) return <Layout title="Procedure"><Spinner /></Layout>;

  const byCategory = items.reduce((acc, p) => { (acc[p.category] = acc[p.category] || []).push(p); return acc; }, {});
  const CAT_LABEL = { redazione: "Redazione offerta", economico: "Valutazione economica", valutazione: "Valutazione tecnica", amministrativo: "Amministrativo", comunicazioni: "Comunicazioni formali" };

  return (
    <Layout title="Procedure guidate" subtitle="Workflow step-by-step per ogni fase della partecipazione">
      <Card className="ai-panel mb-4">
        <div className="flex items-start gap-4">
          <Lightning weight="fill" className="text-violet-600" size={24} />
          <div>
            <Label>Come usare le procedure</Label>
            <p className="text-sm text-zinc-700 mt-1">
              Ogni procedura e una <strong>checklist step-by-step</strong> con azioni concrete e link agli endpoint API.
              Segui le fasi in ordine per scrivere offerta tecnica, calcolare offerta economica, costituire ATI, gestire chiarimenti, sottomettere il plico.
            </p>
          </div>
        </div>
      </Card>

      {Object.entries(byCategory).map(([cat, procs]) => (
        <div key={cat} className="mb-6">
          <div className="label-up mb-2">{CAT_LABEL[cat] || cat}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid={`procs-${cat}`}>
            {procs.map((p) => (
              <Link key={p.id} to={`/procedures/${p.id}`} className="card-flat p-5 block hover:border-klein hover:shadow-md transition-all" data-testid={`proc-${p.id}`}>
                <div className="flex justify-between items-start mb-3">
                  <BookOpen size={22} weight="duotone" className="text-klein" />
                  <span className="font-mono text-xs text-zinc-500 flex items-center gap-1"><Clock size={11} /> {p.estimated_time}</span>
                </div>
                <h3 className="font-display font-bold text-lg tracking-tight mb-1">{p.title}</h3>
                <p className="text-sm text-zinc-600 mb-3 line-clamp-2">{p.description}</p>
                <div className="flex justify-between items-center pt-2 border-t border-zinc-100">
                  <span className="text-xs font-mono text-zinc-500">{p.steps_count} step</span>
                  <ArrowRight size={14} className="text-klein" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      ))}
    </Layout>
  );
}

export function ProcedureDetail() {
  const { pid } = useParams();
  const nav = useNavigate();
  const [proc, setProc] = useState(null);
  const [checked, setChecked] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`proc-${pid}`)) || {}; } catch { return {}; }
  });

  useEffect(() => { api.get(`/procedures/${pid}`).then(({ data }) => setProc(data)); }, [pid]);

  const toggle = (n) => {
    const next = { ...checked, [n]: !checked[n] };
    setChecked(next);
    localStorage.setItem(`proc-${pid}`, JSON.stringify(next));
  };

  if (!proc) return <Layout title="Procedura"><Spinner /></Layout>;

  const done = Object.values(checked).filter(Boolean).length;
  const total = proc.steps.length;
  const pct = Math.round(done / total * 100);

  return (
    <Layout
      title={proc.title}
      subtitle={proc.description}
      actions={<button onClick={() => nav("/procedures")} className="btn-ghost" data-testid="proc-back">← Tutte le procedure</button>}
    >
      <Card className="mb-4">
        <div className="flex items-center justify-between mb-3">
          <Label>Progresso</Label>
          <span className="font-mono text-sm font-semibold">{done}/{total} step</span>
        </div>
        <div className="h-2 bg-zinc-100 rounded-sm overflow-hidden">
          <div className="h-full bg-klein transition-all duration-300" style={{ width: `${pct}%` }} data-testid="progress-bar" />
        </div>
        <div className="text-xs text-zinc-500 mt-2">Tempo stimato: <strong>{proc.estimated_time}</strong></div>
      </Card>

      <div className="space-y-3" data-testid="steps-list">
        {proc.steps.map((s) => (
          <Card key={s.n} className={`transition-all ${checked[s.n] ? "border-emerald-300 bg-emerald-50/30" : ""}`} data-testid={`step-${s.n}`}>
            <div className="flex items-start gap-4">
              <button onClick={() => toggle(s.n)} className="flex-shrink-0 mt-1" data-testid={`step-toggle-${s.n}`}>
                {checked[s.n]
                  ? <CheckCircle weight="fill" className="text-emerald-600" size={26} />
                  : <Circle weight="regular" className="text-zinc-300 hover:text-klein" size={26} />}
              </button>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-mono text-xs text-zinc-500">STEP {String(s.n).padStart(2, "0")}</span>
                </div>
                <h3 className={`font-display text-lg font-bold tracking-tight mb-2 ${checked[s.n] ? "line-through text-zinc-500" : ""}`}>{s.title}</h3>
                {s.actions && (
                  <ul className="text-sm space-y-1 text-zinc-700">
                    {s.actions.map((a, i) => <li key={i} className="flex items-start gap-2"><span className="text-klein mt-0.5">▸</span>{a}</li>)}
                  </ul>
                )}
                {s.endpoint && (
                  <div className="mt-3 text-xs font-mono bg-zinc-100 px-2 py-1 inline-block rounded-sm text-zinc-600">
                    {s.endpoint}
                  </div>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>
    </Layout>
  );
}
